/**
 * Pie del sitio: quiénes somos, cómo contactarnos y dónde estamos.
 *
 * Los datos de contacto van en un `<address>`, que es la etiqueta que existe
 * justamente para eso, y el mapa cierra a la derecha.
 *
 * Las columnas de enlaces al catálogo se sacaron para dejarle el ancho al mapa.
 * Vale saber que eso le quita al pie los enlaces que ayudaban al robot a llegar
 * a las líneas desde cualquier página; hoy esa navegación vive solo en la
 * cabecera y en el mega menú.
 */
import type { ResumenCatalogo } from '@elcarpintero/compartido';
import { Link } from 'react-router-dom';

import { SITIO } from '../../configuracion/sitio';
import { numero } from '../../utilidades/formato';
import { rutas } from '../../utilidades/rutas';
import { registrarClicWhatsapp } from '../../servicios/metricas.servicio';
import { whatsappGeneral } from '../../utilidades/whatsapp';
import { Icono } from '../comunes/Icono';
import { Imagen } from '../comunes/Imagen';
import { MapaTienda } from './MapaTienda';

export interface PieDePaginaProps {
  resumen: ResumenCatalogo;
}

export function PieDePagina({ resumen }: PieDePaginaProps) {
  return (
    <footer className="pie">
      <div className="contenedor">
        <div className="pie-grilla">
          <div>
            <Imagen
              className="logo-marca"
              src={SITIO.logo}
              alt={SITIO.nombreLargo}
              ancho={100}
              alto={50}
            />
            <p className="pie-intro">
              Todo para el carpintero, el mueblista y el tapicero. Más de{' '}
              {numero(resumen.productos)} productos en {numero(resumen.categorias)} categorías, con
              despacho a todo Chile y asesoría real de taller.
            </p>
            <div className="pie-sociales">
              <a
                className="pie-social"
                href={SITIO.redes.instagram}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${SITIO.nombre} en Instagram`}
              >
                <Icono nombre="instagram" />
              </a>
              <a
                className="pie-social"
                href={SITIO.redes.facebook}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`${SITIO.nombre} en Facebook`}
              >
                <Icono nombre="facebook" />
              </a>
              <a
                className="pie-social"
                href={whatsappGeneral()}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Escribir a ${SITIO.nombre} por WhatsApp`}
                onClick={() => registrarClicWhatsapp({ origen: 'pie' })}
              >
                <Icono nombre="wa" />
              </a>
            </div>
          </div>

          <div>
            <h2>Contacto</h2>
            <address>
              <ul>
                <li>
                  {SITIO.direccion.calle}, {SITIO.direccion.comuna}
                </li>
                <li>
                  <a href={`tel:${SITIO.telefono.replace(/\s/g, '')}`}>{SITIO.telefono}</a>
                </li>
                <li>
                  <a href={`mailto:${SITIO.email}`}>{SITIO.email}</a>
                </li>
                <li>{SITIO.horario}</li>
              </ul>
            </address>
            <ul>
              <li>
                <Link to={rutas.nuestraTienda()}>Nuestra tienda y taller</Link>
              </li>
            </ul>
          </div>

          <div className="pie-columna-mapa">
            <h2>Sala de ventas</h2>
            <MapaTienda />
          </div>
        </div>

        <div className="pie-base">
          <small>
            © {new Date().getFullYear()} {SITIO.nombreLargo}. Precios en pesos chilenos con IVA
            incluido.
          </small>
          {/* Los textos legales tienen que estar a mano desde cualquier pagina
              (Ley 19.496 y Ley 21.719), no solo desde el checkout. */}
          <nav className="pie-legal" aria-label="Información legal">
            <Link to={rutas.terminos()}>Términos y condiciones</Link>
            <Link to={rutas.privacidad()}>Política de privacidad</Link>
          </nav>
          <p className="pie-pagos">
            <span className="pago-pil">
              <Icono nombre="tarjeta" />
              WebPay
            </span>
            <span className="pago-pil">
              <Icono nombre="factura" />
              Boleta y factura
            </span>
          </p>
        </div>
      </div>
    </footer>
  );
}
